"use client";

import { Component, useEffect, type ErrorInfo, type ReactNode } from "react";
import { createBrowserReporter, installErrorCollector, type CollectorOptions } from "../browser";

/**
 * Drop-in component: installs the window collector for as long as it is mounted. Renders
 * nothing. Put it once near the root of the app.
 */
export const MemoProjectErrorCollector = (props: CollectorOptions) => {
  const { endpoint, source, dedupeMs } = props;
  useEffect(
    () => installErrorCollector(props),
    // Re-install only when the identity-defining options change; `context` is a getter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [endpoint, source, dedupeMs],
  );
  return null;
};

type BoundaryProps = CollectorOptions & {
  children: ReactNode;
  /** What to render after a render-phase crash. Defaults to nothing (the subtree is dropped). */
  fallback?: ReactNode | ((error: Error, reset: () => void) => ReactNode);
};

type BoundaryState = { error: Error | null };

/**
 * Render-phase errors do not reach `window.onerror` in React — they unmount the tree
 * instead. This boundary reports them (with the component stack) and shows a fallback,
 * so a crash in one panel neither goes unnoticed nor blanks the whole page.
 */
export class MemoProjectErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    const { children: _children, fallback: _fallback, ...options } = this.props;
    createBrowserReporter(options)(error.message, error.stack, {
      componentStack: info.componentStack ?? undefined,
    });
  }

  private reset = () => this.setState({ error: null });

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    const { fallback } = this.props;
    return typeof fallback === "function" ? fallback(error, this.reset) : (fallback ?? null);
  }
}

export { DEFAULT_FLOW_ADDRESS, MemoProjectConnectionForm, STATUS_LABELS } from "./ConnectionForm";
export type { FormSlot } from "./connection/styles";
export type { ConnectionFormProps, ConnectionFormValue } from "./ConnectionForm";

export { SupportPanel } from "./support/SupportPanel";
export type { SupportPanelProps } from "./support/SupportPanel";
export { NewTicketForm, ATTACH_ACCEPT } from "./support/NewTicketForm";
export type { NewTicketFormProps } from "./support/NewTicketForm";
export { TicketDetail } from "./support/TicketDetail";
export type { TicketDetailProps } from "./support/TicketDetail";
export { TicketList } from "./support/TicketList";
export type { TicketListProps } from "./support/TicketList";
export { PriorityChip, StatusChip, PRIORITY_COLORS, STATUS_COLORS } from "./support/ui";
export type { PanelSlot, PanelTheme } from "./support/ui";
export { createSupportApi } from "./support/api";
export type { NewReport, SupportApi, SupportApiOptions, SupportFetch } from "./support/api";
export { useSupportConnection } from "./support/useSupportConnection";
